import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { effectiveValue, billedTotal, paidTotal } from "../lib/constants";
import { formatINR } from "../lib/format";
import { X, Plus, Trash2, Check, Save, IndianRupee, Calendar } from "lucide-react";

const PAYMENT_PILL = {
  paid:     { label: "Paid",     cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  partial:  { label: "Partial",  cls: "bg-amber-500/15 text-amber-400 border-amber-500/20" },
  unbilled: { label: "Unbilled", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20" },
  overdue:  { label: "Overdue",  cls: "bg-red-500/15 text-red-400 border-red-500/20" },
  "n/a":    { label: "—",        cls: "bg-zinc-500/10 text-zinc-600 border-zinc-700/20" },
};

export function PaymentPill({ status, testId }) {
  const p = PAYMENT_PILL[status] || PAYMENT_PILL["n/a"];
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] border rounded-sm ${p.cls}`}
    >
      {p.label}
    </span>
  );
}

export default function PaymentsModal({ row, onClose, onSaved }) {
  const [signedAt, setSignedAt] = useState(row.signed_at || "");
  const [installments, setInstallments] = useState(
    (row.installments || []).map((i) => ({ ...i }))
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const effective = effectiveValue(row);
  const billed = installments.reduce((s, i) => s + (Number(i.amount_cr) || 0), 0);
  const paid = installments.reduce((s, i) => {
    if (!i.paid) return s;
    const amt = i.paid_amount_cr != null && i.paid_amount_cr !== "" ? Number(i.paid_amount_cr) : Number(i.amount_cr || 0);
    return s + (Number.isFinite(amt) ? amt : 0);
  }, 0);
  const outstanding = Math.max(0, effective - paid);
  const billingGap = +(effective - billed).toFixed(2);

  const updateItem = (idx, patch) => {
    setInstallments((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addItem = () => {
    setInstallments((prev) => [
      ...prev,
      { label: "", amount_cr: billingGap > 0 ? billingGap : 0, due_date: "", paid: false, paid_amount_cr: null, notes: "" },
    ]);
  };

  const removeItem = (idx) => {
    setInstallments((prev) => prev.filter((_, i) => i !== idx));
  };

  const togglePaid = (idx) => {
    setInstallments((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        if (it.paid) return { ...it, paid: false, paid_at: null, paid_amount_cr: null };
        return {
          ...it,
          paid: true,
          paid_at: new Date().toISOString().slice(0, 10),
          paid_amount_cr: it.paid_amount_cr ?? it.amount_cr,
        };
      })
    );
  };

  // Quick presets (only when no installments yet)
  const applyPreset = (preset) => {
    if (effective <= 0) return;
    if (preset === "single") {
      setInstallments([{ label: "Full payment", amount_cr: effective, due_date: "", paid: false }]);
    } else if (preset === "50-50") {
      setInstallments([
        { label: "On signing", amount_cr: +(effective / 2).toFixed(2), due_date: "", paid: false },
        { label: "30 days pre-event", amount_cr: +(effective / 2).toFixed(2), due_date: "2026-11-19", paid: false },
      ]);
    } else if (preset === "3-stage") {
      const third = +(effective / 3).toFixed(2);
      const rest = +(effective - 2 * third).toFixed(2);
      setInstallments([
        { label: "On signing", amount_cr: third, due_date: "", paid: false },
        { label: "Mid (October)", amount_cr: third, due_date: "2026-10-15", paid: false },
        { label: "Post-event", amount_cr: rest, due_date: "2027-01-15", paid: false },
      ]);
    }
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put(`/sponsorships/${row.id}`, {
        brand_name: row.brand_name,
        tier: row.tier,
        slots_taken: row.slots_taken,
        status: row.status,
        wave: row.wave,
        notes: row.notes || "",
        closing_price_cr: row.closing_price_cr,
        signed_at: signedAt || null,
        installments: installments.map((i) => ({
          id: i.id,
          label: i.label || "Installment",
          amount_cr: Number(i.amount_cr) || 0,
          due_date: i.due_date || null,
          paid: !!i.paid,
          paid_at: i.paid_at || null,
          paid_amount_cr: i.paid_amount_cr === "" ? null : (i.paid_amount_cr != null ? Number(i.paid_amount_cr) : null),
          notes: i.notes || "",
        })),
      });
      await onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0d0d0d] border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="payments-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-zinc-900">
          <div>
            <div className="overline mb-1">Payments</div>
            <div className="font-display text-xl font-bold leading-tight">{row.brand_name}</div>
            <div className="text-xs text-zinc-500 mt-1">Closing value · {formatINR(effective)}</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:bg-[#181818]" data-testid="payments-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-px bg-zinc-900">
          <div className="bg-[#0d0d0d] p-4">
            <div className="overline mb-1">Billed</div>
            <div className="font-display text-xl tabular">{formatINR(billed)}</div>
            {billingGap !== 0 && (
              <div className={`text-[10px] mt-1 ${billingGap > 0 ? "text-amber-400" : "text-red-400"}`}>
                {billingGap > 0 ? `${formatINR(billingGap)} unbilled` : `${formatINR(Math.abs(billingGap))} over-billed`}
              </div>
            )}
          </div>
          <div className="bg-[#0d0d0d] p-4">
            <div className="overline mb-1">Received</div>
            <div className="font-display text-xl tabular text-emerald-400">{formatINR(paid)}</div>
            <div className="text-[10px] text-zinc-500 mt-1 tabular">
              {effective > 0 ? `${((paid / effective) * 100).toFixed(0)}% of closing` : "—"}
            </div>
          </div>
          <div className="bg-[#0d0d0d] p-4">
            <div className="overline mb-1">Outstanding</div>
            <div className="font-display text-xl tabular text-amber-400">{formatINR(outstanding)}</div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <div>
            <label className="overline block mb-2">Contract signed on</label>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-zinc-500" />
              <input
                type="date"
                value={signedAt || ""}
                onChange={(e) => setSignedAt(e.target.value)}
                className="bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                data-testid="signed-at-input"
              />
              {signedAt && <button onClick={() => setSignedAt("")} className="text-xs text-zinc-500 hover:text-zinc-300">clear</button>}
            </div>
          </div>

          {installments.length === 0 && effective > 0 && (
            <div className="border border-dashed border-zinc-800 p-4">
              <div className="text-sm text-zinc-300 mb-3">No installments set up. Quick start:</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => applyPreset("single")} className="text-xs px-3 py-1.5 border border-zinc-800 hover:bg-[#181818]">Single payment ({formatINR(effective)})</button>
                <button onClick={() => applyPreset("50-50")} className="text-xs px-3 py-1.5 border border-zinc-800 hover:bg-[#181818]">50% signing · 50% pre-event</button>
                <button onClick={() => applyPreset("3-stage")} className="text-xs px-3 py-1.5 border border-zinc-800 hover:bg-[#181818]">3-stage (signing · mid · post-event)</button>
                <button onClick={addItem} className="text-xs px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">Custom</button>
              </div>
            </div>
          )}

          {installments.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="overline">Installments · {installments.length}</div>
                <button onClick={addItem} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300" data-testid="add-installment">
                  <Plus className="h-3.5 w-3.5" /> Add installment
                </button>
              </div>
              {installments.map((it, idx) => (
                <div key={idx} className="border border-zinc-900 bg-[#0a0a0a] p-3" data-testid={`installment-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <input
                      value={it.label || ""}
                      onChange={(e) => updateItem(idx, { label: e.target.value })}
                      placeholder="e.g. On signing"
                      className="col-span-4 bg-transparent border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                    />
                    <div className="col-span-2 flex items-center gap-1">
                      <IndianRupee className="h-3.5 w-3.5 text-zinc-500" />
                      <input
                        type="number" step="0.01" min={0}
                        value={it.amount_cr ?? ""}
                        onChange={(e) => updateItem(idx, { amount_cr: e.target.value === "" ? null : Math.max(0, parseFloat(e.target.value)) })}
                        placeholder="Cr"
                        title="Amount in ₹ Cr"
                        className="w-full bg-transparent border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500 tabular"
                      />
                      <span className="text-xs text-zinc-500">Cr</span>
                    </div>
                    <input
                      type="date"
                      value={it.due_date || ""}
                      onChange={(e) => updateItem(idx, { due_date: e.target.value })}
                      className="col-span-3 bg-transparent border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                    />
                    <button
                      onClick={() => togglePaid(idx)}
                      className={`col-span-2 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs border transition-colors ${
                        it.paid
                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                          : "border-zinc-800 text-zinc-400 hover:bg-[#181818]"
                      }`}
                      data-testid={`toggle-paid-${idx}`}
                    >
                      <Check className="h-3.5 w-3.5" /> {it.paid ? "Paid" : "Mark paid"}
                    </button>
                    <button
                      onClick={() => removeItem(idx)}
                      className="col-span-1 p-1.5 text-red-400 hover:bg-[#1a1a1a] justify-self-end"
                      data-testid={`remove-installment-${idx}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {it.paid && (
                    <div className="grid grid-cols-12 gap-2 mt-2 items-center">
                      <div className="col-span-4 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Received on</div>
                      <input
                        type="date"
                        value={it.paid_at || ""}
                        onChange={(e) => updateItem(idx, { paid_at: e.target.value })}
                        className="col-span-3 bg-transparent border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                      />
                      <div className="col-span-3 flex items-center gap-1">
                        <IndianRupee className="h-3.5 w-3.5 text-zinc-500" />
                        <input
                          type="number" step="0.01" min={0}
                          value={it.paid_amount_cr ?? ""}
                          onChange={(e) => updateItem(idx, { paid_amount_cr: e.target.value === "" ? null : Math.max(0, parseFloat(e.target.value)) })}
                          placeholder={it.amount_cr ?? ""}
                          title="Actual received amount in ₹ Cr (defaults to scheduled)"
                          className="w-full bg-transparent border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500 tabular"
                        />
                        <span className="text-xs text-zinc-500">Cr</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-900 px-6 py-4 flex items-center justify-between">
          {err ? (
            <div className="text-sm text-red-400">{err}</div>
          ) : (
            <div className="text-xs text-zinc-500">Changes apply when you save.</div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-800 text-zinc-300 hover:bg-[#181818]">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="payments-save"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-4 py-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save payments"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
